import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	ChevronLeftOutline,
	ChevronRightOutline,
	Display,
	HStack,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalOverlay,
	rem,
	Stack,
	Text,
	XOutline,
} from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { activePromosQueryKey, useActivePromos } from '../../queries/promos';
import { ActivePromo } from '../../schemas/promos';
import { useUserContentDismissal } from '../profile/user-content-dismissal';

const PromoMedia = ({ promo, height }: { promo: ActivePromo; height: number }) => {
	if (!promo.imageUrl) return null;

	if (promo.mediaType === 'video') {
		return (
			<Box
				as="video"
				src={promo.imageUrl}
				autoPlay
				loop
				muted
				playsInline
				w="100%"
				maxH={rem(height)}
				display="block"
				objectFit="cover"
				sx={{ '&::-webkit-media-controls': { display: 'none' } }}
			/>
		);
	}

	return <Box as="img" src={promo.imageUrl} alt="" w="100%" maxH={rem(height)} display="block" objectFit="cover" />;
};

// Matches Promos::Promo::DISMISSAL_NAMESPACE on the backend, which is also
// the default namespace for <PromoModal>. Keeping these in sync means the
// server-side filter in ListActivePromosForViewer naturally hides any promo
// dismissed via this card or any other surface that uses the same key.
const DISMISSAL_NAMESPACE = 'promo';

// Feature flag that gates the entire dynamic promo system on the FE. When
// off, no API call is made and the carousel never renders, regardless of
// what the backend would return. NOTE: the Amplitude key is spelled with
// a typo (`annoucements`, missing an `n`); keep this constant in lockstep
// with whatever Amplitude has, or rename the flag in Amplitude first and
// update both at the same time.
//
// Exported because other surfaces (e.g. legacy office-hours cards) want
// to hide themselves when the dynamic promo system is taking over.
export const IN_APP_FEATURE_ANNOUNCEMENTS_FLAG = 'in-app-feature-annoucements';
const FEATURE_FLAG = IN_APP_FEATURE_ANNOUNCEMENTS_FLAG;

// Optional carousel controls passed in by <DynamicPromos> when there is
// more than one eligible promo. When present, the card renders prev/next
// chevrons and dot indicators so the user can step through the list.
interface CarouselControls {
	current: number;
	total: number;
	onPrev: () => void;
	onNext: () => void;
	onJumpTo: (index: number) => void;
}

interface DynamicPromoProps {
	promo: ActivePromo;
	carousel?: CarouselControls;
}

// Renders a single dynamic promo as a sidebar teaser card with a "Learn more"
// button that opens a modal showing the full body. Closing the modal or
// clicking the X on the card persists a dismissal under the `promo:<name>`
// key so the backend will stop returning it on subsequent loads.
export const DynamicPromo = ({ promo, carousel }: DynamicPromoProps) => {
	const { t } = useTranslation('components', { keyPrefix: 'dynamicPromo' });
	const [isModalOpen, setIsModalOpen] = useState(false);
	const queryClient = useQueryClient();

	const { updateUserContentDismissal } = useUserContentDismissal({
		name: promo.name,
		namespace: DISMISSAL_NAMESPACE,
	});

	const dismiss = () => {
		updateUserContentDismissal();
		setIsModalOpen(false);
		// Optimistically remove the card from the list while the dismissal
		// POST is in flight, instead of waiting for the next refetch.
		queryClient.setQueryData<ActivePromo[]>(activePromosQueryKey(), (current) => (current ?? []).filter((p) => p.id !== promo.id));
	};

	return (
		<>
			<Stack
				bgColor="bg.surface"
				borderRadius="md"
				border="1px solid"
				borderColor="border.default"
				px={2}
				pt={8}
				pb={4}
				spacing={3}
				overflow="hidden"
				position="relative"
				data-testid={`dynamic-promo-${promo.name}`}
			>
				<Box position="absolute" top={1} right={1}>
					<IconButton
						aria-label={t('dismiss')}
						icon={<XOutline boxSize={rem(14)} />}
						size="xs"
						variant="ghost"
						onClick={dismiss}
						data-testid={`dynamic-promo-${promo.name}-dismiss`}
					/>
				</Box>

				<Text fontSize={14} fontWeight="bold" color="text.default" px={3}>
					{t('section_title')}
				</Text>

				{promo.imageUrl && (
					<Box
						borderRadius="sm"
						border="1px solid"
						borderColor="border.soft"
						bg="bg.soft"
						w="100%"
						maxH={rem(130)}
						overflow="hidden"
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<PromoMedia promo={promo} height={130} />
					</Box>
				)}

				<Stack spacing={0.5}>
					<Text fontSize={14} fontWeight="semibold" color="text.default" noOfLines={2}>
						{promo.title}
					</Text>
					<Text fontSize={12} color="text.softer" noOfLines={3}>
						{promo.body}
					</Text>
				</Stack>

				<Button
					variant="link"
					size="sm"
					colorScheme="primary"
					alignSelf="flex-start"
					onClick={() => setIsModalOpen(true)}
					data-testid={`dynamic-promo-${promo.name}-open`}
				>
					{t('learn_more')}
				</Button>

				{carousel && (
					<HStack spacing={1} justify="center" data-testid={`dynamic-promo-${promo.name}-carousel`}>
						<IconButton
							aria-label={t('previous')}
							icon={<ChevronLeftOutline boxSize={rem(14)} />}
							size="xs"
							variant="ghost"
							onClick={carousel.onPrev}
							data-testid={`dynamic-promo-${promo.name}-prev`}
						/>
						<HStack spacing={1.5}>
							{Array.from({ length: carousel.total }).map((_, index) => (
								<Box
									key={index}
									as="button"
									aria-label={t('go_to', { number: index + 1 })}
									aria-current={index === carousel.current ? 'true' : undefined}
									w={2}
									h={2}
									borderRadius="full"
									bg={index === carousel.current ? 'primary.bg' : 'secondary.bgAlphaActive'}
									onClick={() => carousel.onJumpTo(index)}
								/>
							))}
						</HStack>
						<IconButton
							aria-label={t('next')}
							icon={<ChevronRightOutline boxSize={rem(14)} />}
							size="xs"
							variant="ghost"
							onClick={carousel.onNext}
							data-testid={`dynamic-promo-${promo.name}-next`}
						/>
					</HStack>
				)}
			</Stack>

			<Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="md" isCentered autoFocus={false} closeOnOverlayClick>
				<ModalOverlay />
				<ModalContent overflow="hidden" borderRadius="lg" pb={3}>
					<ModalCloseButton zIndex={1} />
					<Box bg="bg.surface" h={rem(48)} />
					{promo.imageUrl && (
						<Box bg="bg.soft" px={5} py={6}>
							<PromoMedia promo={promo} height={320} />
						</Box>
					)}
					<ModalBody as={Stack} gap={4} pt={7} px={6} textAlign="left">
						<Display color="text.default" size="xs">
							{promo.title}
						</Display>
						<Text color="text.soft">{promo.body}</Text>
						{promo.ctaLabel && promo.ctaUrl ? (
							<Button
								as="a"
								href={promo.ctaUrl}
								// Open in a new tab so the current page stays mounted
								// and the dismissal POST fired by `dismiss` isn't
								// cancelled by an unload.
								target="_blank"
								rel="noopener noreferrer"
								colorScheme="primary"
								alignSelf="flex-start"
								onClick={dismiss}
								data-testid={`dynamic-promo-${promo.name}-modal-cta`}
							>
								{promo.ctaLabel}
							</Button>
						) : (
							<Button colorScheme="primary" alignSelf="flex-start" onClick={dismiss} data-testid={`dynamic-promo-${promo.name}-modal-dismiss`}>
								{t('got_it')}
							</Button>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
};

interface DynamicPromosProps {
	isExpanded?: boolean;
}

export const DynamicPromos = ({ isExpanded = true }: DynamicPromosProps = {}) => {
	const isFeatureEnabled = useFeatureFlag(FEATURE_FLAG);
	const { data: promos } = useActivePromos({ enabled: isExpanded && isFeatureEnabled });
	const [currentIndex, setCurrentIndex] = useState(0);

	// Clamp the index when the visible list shrinks (dismissal) or changes.
	useEffect(() => {
		if (!promos?.length) return;
		if (currentIndex > promos.length - 1) setCurrentIndex(promos.length - 1);
	}, [promos, currentIndex]);

	if (!isExpanded || !isFeatureEnabled || !promos?.length) return null;

	const safeIndex = Math.min(currentIndex, promos.length - 1);
	const currentPromo = promos[safeIndex];
	const total = promos.length;

	const carousel: CarouselControls | undefined =
		total > 1
			? {
					current: safeIndex,
					total,
					onPrev: () => setCurrentIndex((i) => (i <= 0 ? total - 1 : i - 1)),
					onNext: () => setCurrentIndex((i) => (i + 1) % total),
					onJumpTo: setCurrentIndex,
				}
			: undefined;

	return <DynamicPromo promo={currentPromo} carousel={carousel} />;
};
